import { useEffect, useState, type CSSProperties } from "react";
import { useOutletContext } from "react-router-dom";
import {
  adminListActivityToday,
  adminListActivityPopular,
  adminResetTodayActivity,
  adminFeatureToday,
  type ActivityTodayRow,
  type ActivityPopularRow,
} from "../../lib/api";
import type { SessionUser } from "../../types/auth";
import { useConfirmDialog } from "../../components/ConfirmDialog";
import OperationsStudioTabs from "../../components/admin/OperationsStudioTabs";

/**
 * Admin Activity — Today + Popular views.
 * - Today: per-drink counts (search/load/pin) for the current service date.
 * - Popular: 30-day rolling totals.
 *
 * Owner can Reset Today's counts on a drink (signal-data nuke).
 * Manager+ can Feature/Unfeature a drink for today.
 */

type View = "today" | "popular";

export default function AdminActivity() {
  const { user } = useOutletContext<{ user: SessionUser }>();
  const isOwner = (user.bar_roles ?? []).includes("bar_owner");

  const [view, setView] = useState<View>("today");
  const [today, setToday] = useState<{ rows: ActivityTodayRow[]; service_date: string } | null>(null);
  const [popular, setPopular] = useState<{ rows: ActivityPopularRow[]; service_date_start: string; service_date_end: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  function showFlash(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3000);
  }

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      if (view === "today") {
        const r = await adminListActivityToday();
        setToday({ rows: r.rows, service_date: r.service_date });
      } else {
        const r = await adminListActivityPopular();
        setPopular({
          rows: r.rows,
          service_date_start: r.service_date_start,
          service_date_end: r.service_date_end,
        });
      }
    } catch (err: any) {
      setError(err?.message ?? "Could not load activity");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, [view]);

  async function onReset(drinkId: string, drinkName: string) {
    if (!await confirm({
      title: `Reset today's activity counts for "${drinkName}"?`,
      message: "This zeros search/load/pin for today only.",
      confirmLabel: "Reset",
      danger: true,
    })) return;
    setBusyId(drinkId);
    try {
      await adminResetTodayActivity(drinkId);
      showFlash(`Reset today's counts for ${drinkName}.`);
      reload();
    } catch (err: any) {
      setError(err?.message ?? "Could not reset");
    } finally {
      setBusyId(null);
    }
  }

  async function onFeatureToggle(drinkId: string, drinkName: string, makeFeatured: boolean) {
    setBusyId(drinkId);
    try {
      await adminFeatureToday(drinkId, makeFeatured);
      showFlash(makeFeatured ? `Featured ${drinkName}.` : `Unfeatured ${drinkName}.`);
      reload();
    } catch (err: any) {
      setError(err?.message ?? "Could not update featured status");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section style={styles.shell}>
      <OperationsStudioTabs activeTab="activity" badgeCounts={{ activity: today?.rows?.length }} />
      <p style={styles.eyebrow}>Service controls</p>
      <h1 style={styles.title}>Activity</h1>

      <div style={styles.tabs}>
        <button
          type="button"
          onClick={() => setView("today")}
          style={view === "today" ? styles.tabActive : styles.tab}
        >
          Today
          {today && <span style={styles.tabHint}>{today.service_date}</span>}
        </button>
        <button
          type="button"
          onClick={() => setView("popular")}
          style={view === "popular" ? styles.tabActive : styles.tab}
        >
          Popular (30 days)
        </button>
      </div>

      {flash && <div role="status" style={styles.flash}>{flash}</div>}
      {error && <div style={styles.error}>{error}</div>}
      {loading && <p style={styles.muted}>Loading activity…</p>}

      {!loading && view === "today" && today && (
        <TodayTable
          rows={today.rows}
          isOwner={isOwner}
          busyId={busyId}
          onFeatureToggle={onFeatureToggle}
          onReset={onReset}
        />
      )}

      {!loading && view === "popular" && popular && (
        <PopularTable rows={popular.rows} window={`${popular.service_date_start} → ${popular.service_date_end}`} />
      )}
      {confirmDialog}
    </section>
  );
}

function TodayTable({
  rows,
  isOwner,
  busyId,
  onFeatureToggle,
  onReset,
}: {
  rows: ActivityTodayRow[];
  isOwner: boolean;
  busyId: string | null;
  onFeatureToggle: (drinkId: string, drinkName: string, makeFeatured: boolean) => void;
  onReset: (drinkId: string, drinkName: string) => void;
}) {
  if (rows.length === 0) {
    return <p style={styles.muted}>No drinks loaded yet today.</p>;
  }
  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Drink</th>
            <th style={styles.thRight}>Search</th>
            <th style={styles.thRight}>Load</th>
            <th style={styles.thRight}>Pin</th>
            <th style={styles.thRight}>Total</th>
            <th style={styles.thCenter}>Featured</th>
            <th style={styles.thRight}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.drink_id} style={styles.tr}>
              <td style={styles.td}>
                <strong>{r.name}</strong>
                <div style={styles.subnote}>{r.service_status}{!r.has_approved && " · no approved"}</div>
              </td>
              <td style={styles.tdRight}>{r.search_count}</td>
              <td style={styles.tdRight}>{r.load_count}</td>
              <td style={styles.tdRight}>{r.pin_count}</td>
              <td style={styles.tdRightStrong}>{r.total_today}</td>
              <td style={styles.tdCenter}>
                {r.is_featured_today
                  ? <span style={styles.featuredChip}>Featured</span>
                  : <span style={styles.subnote}>—</span>}
              </td>
              <td style={styles.tdRight}>
                <button
                  type="button"
                  onClick={() => onFeatureToggle(r.drink_id, r.name, !r.is_featured_today)}
                  disabled={busyId === r.drink_id || !r.has_approved}
                  style={r.is_featured_today ? styles.btnSecondary : styles.btnGold}
                  title={!r.has_approved ? "Drink has no Approved version" : ""}
                >
                  {r.is_featured_today ? "Unfeature" : "Feature"}
                </button>
                {isOwner && (
                  <button
                    type="button"
                    onClick={() => onReset(r.drink_id, r.name)}
                    disabled={busyId === r.drink_id || r.total_today === 0}
                    style={styles.btnRed}
                  >
                    Reset
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PopularTable({ rows, window: dateWindow }: { rows: ActivityPopularRow[]; window: string }) {
  if (rows.length === 0) {
    return <p style={styles.muted}>No activity in the last 30 days.</p>;
  }
  return (
    <>
      <p style={styles.muted}>{dateWindow}</p>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Drink</th>
              <th style={styles.thRight}>Search 30d</th>
              <th style={styles.thRight}>Load 30d</th>
              <th style={styles.thRight}>Pin 30d</th>
              <th style={styles.thRight}>Total 30d</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.drink_id} style={styles.tr}>
                <td style={styles.td}>
                  <strong>{r.name}</strong>
                  <div style={styles.subnote}>{r.service_status}</div>
                </td>
                <td style={styles.tdRight}>{r.search_30}</td>
                <td style={styles.tdRight}>{r.load_30}</td>
                <td style={styles.tdRight}>{r.pin_30}</td>
                <td style={styles.tdRightStrong}>{r.total_30}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const styles: Record<string, CSSProperties> = {
  shell: { maxWidth: 1200, padding: "8px 0 32px" },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    margin: "8px 0 16px",
    letterSpacing: -0.4,
    color: "var(--text)",
  },
  tabs: { display: "flex", gap: 4, marginBottom: 16, flexWrap: "wrap" },
  tab: {
    background: "var(--bg)",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  tabActive: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: "1px solid var(--gold)",
    borderRadius: 10,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  tabHint: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 10,
    opacity: 0.7,
  },
  muted: { color: "var(--muted)", fontSize: 13 },
  flash: {
    margin: "0 0 12px",
    padding: 10,
    background: "rgba(39,174,96,0.12)",
    border: "1px solid rgba(39,174,96,0.4)",
    color: "#86efac",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
  },
  error: {
    margin: "0 0 12px",
    padding: 10,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 10,
    fontSize: 12,
  },
  tableWrap: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    overflowX: "auto",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    borderBottom: "1px solid var(--border)",
  },
  thRight: {
    textAlign: "right",
    padding: "10px 12px",
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    borderBottom: "1px solid var(--border)",
  },
  thCenter: {
    textAlign: "center",
    padding: "10px 12px",
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    borderBottom: "1px solid var(--border)",
  },
  tr: { borderBottom: "1px solid var(--border)" },
  td: { padding: "10px 12px", color: "var(--text)" },
  tdCenter: { padding: "10px 12px", textAlign: "center", color: "var(--text)" },
  tdRight: { padding: "10px 12px", textAlign: "right", color: "var(--text)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
  tdRightStrong: {
    padding: "10px 12px",
    textAlign: "right",
    color: "var(--text)",
    fontWeight: 800,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  subnote: { color: "var(--muted)", fontSize: 11, marginTop: 2 },
  featuredChip: {
    background: "rgba(249,175,54,0.18)",
    color: "var(--gold)",
    border: "1px solid rgba(249,175,54,0.4)",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  btnGold: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 8,
    padding: "8px 12px",
    minHeight: 40,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    marginRight: 6,
    whiteSpace: "nowrap",
  },
  btnSecondary: {
    background: "transparent",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "8px 12px",
    minHeight: 40,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    marginRight: 6,
    whiteSpace: "nowrap",
  },
  btnRed: {
    background: "rgba(224,80,64,0.16)",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 8,
    padding: "8px 12px",
    minHeight: 40,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
};
